import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { g, p, del, errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Hourglass, Plus, Trash2, MessageCircle, CheckCircle2, Star, BellRing, X } from "lucide-react";

const vuota = { customer_name: "", phone: "", people: 2, time: "", note: "" };

export const ListaAttesa = ({ data, zones = [], onConvertita }) => {
  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [nuovo, setNuovo] = useState(vuota);

  const load = useCallback(async () => {
    const [r, a] = await Promise.all([g("/waitlist", { date: data }), g("/waitlist/alerts")]);
    setRows(r);
    setAlerts(a);
  }, [data]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const aggiungi = async () => {
    if (!nuovo.customer_name.trim()) return toast.error("Scrivi almeno il nome");
    try {
      await p("/waitlist", { ...nuovo, people: Number(nuovo.people) || 2, date: data });
      toast.success("Aggiunto alla lista d'attesa");
      setNuovo(vuota);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="lista-attesa">
      {alerts.map((a) => (
        <Card key={a.id} className="border-emerald-300 bg-emerald-50 p-4" data-testid={`posto-libero-${a.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-bold text-emerald-900">
                <BellRing className="mr-1.5 inline h-4 w-4" /> Si è liberato un posto: {a.people} persone alle {a.time}
              </p>
              <p className="mt-0.5 text-xs text-emerald-800">
                {a.freed_from ? `${a.freed_from} · ` : ""}
                {a.reason}. Il primo compatibile in lista:
              </p>
            </div>
            <button
              className="text-emerald-700"
              data-testid={`posto-libero-chiudi-${a.id}`}
              onClick={async () => {
                await p(`/waitlist/alerts/${a.id}/seen`, {});
                load();
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-2 space-y-1">
            {a.candidates.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-white p-2 text-xs">
                <span className="font-bold">{c.name}</span>
                {c.abituale && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-900">
                    <Star className="mr-0.5 inline h-2.5 w-2.5" /> abituale
                  </span>
                )}
                <span className="text-slate-500">
                  {c.people} persone {c.time && `· ${c.time}`}
                </span>
                {c.whatsapp && (
                  <a
                    className="ml-auto font-semibold text-emerald-700 hover:underline"
                    href={c.whatsapp}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`avvisa-${c.id}`}
                    onClick={() => p(`/waitlist/${c.id}/offer`, {}).then(load)}
                  >
                    <MessageCircle className="mr-1 inline h-3.5 w-3.5" /> Avvisa
                  </a>
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}

      <Card className="space-y-3 p-4">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
          <Hourglass className="mr-1.5 inline h-3.5 w-3.5" /> Metti in lista d'attesa
        </p>
        <div className="grid gap-2 sm:grid-cols-5">
          <Input
            placeholder="Nome"
            value={nuovo.customer_name}
            onChange={(e) => setNuovo({ ...nuovo, customer_name: e.target.value })}
            data-testid="attesa-nome"
          />
          <Input
            type="tel"
            placeholder="Telefono"
            value={nuovo.phone}
            onChange={(e) => setNuovo({ ...nuovo, phone: e.target.value })}
            data-testid="attesa-telefono"
          />
          <Input
            type="number"
            min="1"
            placeholder="Persone"
            value={nuovo.people}
            onChange={(e) => setNuovo({ ...nuovo, people: e.target.value })}
            data-testid="attesa-persone"
          />
          <Input
            type="time"
            value={nuovo.time}
            onChange={(e) => setNuovo({ ...nuovo, time: e.target.value })}
            data-testid="attesa-ora"
          />
          <Button onClick={aggiungi} data-testid="attesa-aggiungi">
            <Plus className="mr-1 h-4 w-4" /> In lista
          </Button>
        </div>
        <p className="text-xs text-slate-400">
          L'ordine è quello di richiesta; a parità di orario passa avanti chi è cliente abituale.
        </p>
      </Card>

      <div className="space-y-2">
        {rows.map((w) => (
          <Card key={w.id} className="p-4" data-testid={`attesa-${w.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-num rounded-md bg-slate-900 px-2 py-0.5 text-xs font-bold text-white">
                {w.position}°
              </span>
              <span className="font-semibold">{w.customer_name}</span>
              {w.abituale && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-900" data-testid={`attesa-abituale-${w.id}`}>
                  <Star className="mr-0.5 inline h-2.5 w-2.5" /> abituale
                </span>
              )}
              {w.status === "avvisato" && (
                <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-900">avvisato</span>
              )}
              <span className="text-xs text-slate-500">
                {w.people} persone {w.time && `· dalle ${w.time}`}
              </span>
              <div className="ml-auto flex items-center gap-2">
                {w.whatsapp && (
                  <a
                    href={w.whatsapp}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-emerald-700"
                    data-testid={`attesa-whatsapp-${w.id}`}
                    onClick={() => p(`/waitlist/${w.id}/offer`, {}).then(load)}
                  >
                    <MessageCircle className="mr-1 inline h-3.5 w-3.5" /> Avvisa
                  </a>
                )}
                <Button
                  size="sm"
                  className="h-8"
                  data-testid={`attesa-conferma-${w.id}`}
                  onClick={async () => {
                    try {
                      await p(`/waitlist/${w.id}/convert`, {});
                      toast.success(`${w.customer_name} è ora una prenotazione`);
                      load();
                      onConvertita?.();
                    } catch (e) {
                      toast.error(errMsg(e));
                    }
                  }}
                >
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Prenota
                </Button>
                <button
                  className="text-slate-400 hover:text-rose-600"
                  data-testid={`attesa-elimina-${w.id}`}
                  onClick={async () => {
                    await del(`/waitlist/${w.id}`);
                    load();
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {w.note && <p className="mt-1 text-xs text-slate-500">{w.note}</p>}
          </Card>
        ))}
        {!rows.length && (
          <p className="py-6 text-center text-sm text-slate-500">
            Nessuno in lista d'attesa per questa data.
          </p>
        )}
      </div>
    </div>
  );
};
